import { z } from 'zod';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { format } from 'date-fns';
import { ALL_COUNTRIES } from './GDPRConsent/GDPRConsentData';


const POSTAL_PATTERNS: Record<string, RegExp> = {
  // North America
  'United States': /^\d{5}(-\d{4})?$/,
  'Canada': /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/,
  'Mexico': /^\d{5}$/,

  // Europe
  'United Kingdom': /^(GIR 0AA|[A-Za-z]{1,2}\d[A-Za-z\d]? ?\d[A-Za-z]{2})$/i,
  'Germany': /^\d{5}$/,
  'France': /^\d{5}$/,
  'Italy': /^\d{5}$/,
  'Spain': /^\d{5}$/,
  'Portugal': /^\d{4}-\d{3}$/,
  'Netherlands': /^\d{4} ?[A-Za-z]{2}$/,
  'Belgium': /^\d{4}$/,
  'Switzerland': /^\d{4}$/,
  'Austria': /^\d{4}$/,
  'Sweden': /^\d{3} ?\d{2}$/,
  'Norway': /^\d{4}$/,
  'Denmark': /^\d{4}$/,
  'Finland': /^\d{5}$/,
  'Poland': /^\d{2}-\d{3}$/,
  'Czech Republic': /^\d{3} ?\d{2}$/,
  'Hungary': /^\d{4}$/,
  'Romania': /^\d{6}$/,
  'Bulgaria': /^\d{4}$/,
  'Greece': /^\d{3} ?\d{2}$/,
  'Croatia': /^\d{5}$/,
  'Slovakia': /^\d{3} ?\d{2}$/,
  'Slovenia': /^\d{4}$/,
  'Estonia': /^\d{5}$/,
  'Latvia': /^(LV-)?\d{4}$/,
  'Lithuania': /^(LT-)?\d{5}$/,
  'Luxembourg': /^\d{4}$/,
  'Ireland': /^[A-Za-z]\d{2} ?[A-Za-z\d]{4}$/,
  'Iceland': /^\d{3}$/,
  'Liechtenstein': /^\d{4}$/,
  'Malta': /^[A-Za-z]{3} ?\d{4}$/,
  'Cyprus': /^\d{4}$/,
  'Serbia': /^\d{5}$/,
  'Bosnia and Herzegovina': /^\d{5}$/,
  'Albania': /^\d{4}$/,
  'Ukraine': /^\d{5}$/,
  'Russia': /^\d{6}$/,
  'Turkey': /^\d{5}$/,
  'Belarus': /^\d{6}$/,
  'Moldova': /^(MD-)?\d{4}$/,

  // Asia-Pacific
  'Australia': /^\d{4}$/,
  'New Zealand': /^\d{4}$/,
  'India': /^\d{6}$/,
  'Japan': /^\d{3}-\d{4}$/,
  'China': /^\d{6}$/,
  'South Korea': /^\d{5}$/,
  'Singapore': /^\d{6}$/,
  'Malaysia': /^\d{5}$/,
  'Thailand': /^\d{5}$/,
  'Indonesia': /^\d{5}$/,
  'Philippines': /^\d{4}$/,
  'Vietnam': /^\d{6}$/,
  'Pakistan': /^\d{5}$/,
  'Bangladesh': /^\d{4}$/,
  'Sri Lanka': /^\d{5}$/,
  'Nepal': /^\d{5}$/,
  'Taiwan': /^\d{3}(\d{2,3})?$/,
  'Hong Kong': /^(999077|[0-9]{6})?$/,  // HK has no mandatory postal code; accept empty or digits
  'Maldives': /^\d{5}$/,
  'Brunei': /^[A-Za-z]{2}\d{4}$/,

  // Middle East & Africa
  'Saudi Arabia': /^\d{5}$/,
  'United Arab Emirates': /^\d{5}$/,
  'Israel': /^\d{7}$/,
  'Qatar': /^\d{5}$/,
  'Kuwait': /^\d{5}$/,
  'Oman': /^\d{3}$/,
  'Jordan': /^\d{5}$/,
  'Lebanon': /^(\d{4}([ ]?\d{4})?)?$/,
  'Iran': /^\d{5}-?\d{5}$/,
  'Iraq': /^\d{5}$/,
  'South Africa': /^\d{4}$/,
  'Egypt': /^\d{5}$/,
  'Morocco': /^\d{5}$/,
  'Tunisia': /^\d{4}$/,
  'Algeria': /^\d{5}$/,
  'Nigeria': /^\d{6}$/,
  'Kenya': /^\d{5}$/,
  'Ghana': /^[A-Za-z]{2}-\d{4}-\d{4}$/,
  'Ethiopia': /^\d{4}$/,
  'Tanzania': /^\d{5}$/,
  'Uganda': /^\d{5}$/,

  // Latin America
  'Brazil': /^\d{5}-?\d{3}$/,
  'Argentina': /^[A-Za-z]\d{4}[A-Za-z]{3}$/,
  'Chile': /^\d{7}$/,
  'Colombia': /^\d{6}$/,
  'Peru': /^\d{5}$/,
  'Venezuela': /^\d{4}$/,
  'Ecuador': /^\d{6}$/,
  'Bolivia': /^\d{4}$/,
  'Paraguay': /^\d{4}$/,
  'Uruguay': /^\d{5}$/,
  'Costa Rica': /^\d{4,5}$/,
  'Guatemala': /^\d{5}$/,
  'Honduras': /^([A-Za-z]{2})\d{4}$/,
  'El Salvador': /^[A-Za-z]{2}-\d{4}$/,
  'Dominican Republic': /^\d{5}$/,
  'Cuba': /^\d{5}$/,
  'Jamaica': /^[A-Za-z]{2}\d{2}$/,
};

const GENERIC_POSTAL_RE = /^[A-Za-z0-9][A-Za-z0-9\s\-]{1,9}$/;

const NO_POSTAL_CODE_COUNTRIES = new Set([
  'Eritrea', 'Libya', 'North Korea', 'Palestinian Territory',
  'Solomon Islands', 'Tokelau', 'Vatican City',
]);

const validatePostalCode = (zip: string, country: string): boolean => {
  if (NO_POSTAL_CODE_COUNTRIES.has(country)) return true;
  const pattern = POSTAL_PATTERNS[country];
  if (pattern) return pattern.test(zip.trim());
  return GENERIC_POSTAL_RE.test(zip.trim());
};

const US_STATES = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA',
  'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT',
  'VA', 'WA', 'WV', 'WI', 'WY', 'DC', 'AS', 'GU', 'MP', 'PR', 'VI',
]);

const CA_PROVINCES = new Set([
  'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT',
]);

const validateState = (state: string, country: string): { ok: boolean; message: string } => {
  const s = state.trim();
  if (country === 'United States') {
    if (US_STATES.has(s.toUpperCase()))
      return { ok: true, message: '' };
    return { ok: false, message: 'Enter a valid 2-letter US state abbreviation (e.g. CA, TX, NY).' };
  }
  if (country === 'Canada') {
    if (CA_PROVINCES.has(s.toUpperCase()))
      return { ok: true, message: '' };
    return { ok: false, message: 'Enter a valid 2-letter Canadian province abbreviation (e.g. ON, BC, QC).' };
  }
  // International free-form: 2-50 chars, letters / spaces / hyphens / periods
  if (s.length < 2)
    return { ok: false, message: 'State / Province must be at least 2 characters.' };
  if (s.length > 50)
    return { ok: false, message: 'State / Province must be no more than 50 characters.' };
  if (!/^[A-Za-z\s\-.']+$/.test(s))
    return { ok: false, message: 'State / Province must contain only letters, spaces, hyphens, or periods.' };
  return { ok: true, message: '' };
};

export const PersonalDetailsSchema = z.object({
  firstName: z
    .string()
    .min(2, 'First Name Must be at least 2 characters long')
    .regex(/^[A-Za-z\s]+$/, 'First Name must contain only letters and spaces.'),

  middleName: z
    .string()
    .regex(/^[A-Za-z\s]*$/, 'Middle Name must contain only letters and spaces.')
    .optional()
    .default(''),

  lastName: z
    .string()
    .min(2, 'Last Name must be at least 2 characters long.')
    .regex(/^[A-Za-z\s]+$/, 'Last Name must contain only letters and spaces.'),

  dob: z
    .string()
    .min(1, 'Date of Birth is required.')
    .refine((val) => {
      if (!val) return false;
      const d = new Date(val);
      return !isNaN(d.getTime());
    }, 'Please enter a valid date of birth.')
    .refine((val) => {
      const d = new Date(val);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return d <= today;
    }, 'Date of Birth cannot be a future date.')
    .refine((val) => {
      const d = new Date(val);
      const minDate = new Date();
      minDate.setFullYear(minDate.getFullYear() - 120);
      return d >= minDate;
    }, 'Please enter a valid date of birth (max 120 years ago).')
    .transform((val) => {
      // Normalise any valid date string → MM/dd/yyyy (backend format)
      if (!val) return val;
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) return val; // already correct
      try {
        const d = new Date(val);
        if (!isNaN(d.getTime())) return format(d, 'MM/dd/yyyy');
      } catch { /* fall through */ }
      return val;
    }),

  phone: z
    .string()
    .min(1, 'Phone is required.')
    .refine(
      (v) => v.replace(/\D/g, '').length >= 6 && isValidPhoneNumber(v),
      { message: 'Please enter a valid phone number.' }
    ),

  gender: z.enum(['Male', 'Female', 'Other'], {
    errorMap: () => ({ message: 'Gender is required.' }),
  }),

  email: z
    .string()
    .min(1, 'Email is required.')
    .email('Please enter a valid email address.'),

  addressLine1: z
    .string()
    .min(5, 'Address Line 1 must be at least 5 characters.')
    .max(100, 'Address Line 1 must be no more than 100 characters.'),

  addressLine2: z.string().optional().default(''),

  city: z.string().min(1, 'City is required.'),

  // State validated contextually against country (cross-field via superRefine)
  state: z
    .string()
    .min(1, 'State / Province is required.'),

  // Country must be one of the known countries in the app's list
  country: z
    .string()
    .min(1, 'Country is required.')
    .refine(
      (val) => (ALL_COUNTRIES as string[]).includes(val.trim()),
      { message: 'Please enter a valid country name.' }
    ),

  // Zip validated contextually against country (cross-field via superRefine)
  zipCode: z
    .string()
    .min(1, 'Zip / Postal Code is required.'),

  PK: z.string().optional().default(''),
}).superRefine((data, ctx) => {
  // ── State / Province cross-field validation ──
  const stateResult = validateState(data.state, data.country);
  if (!stateResult.ok) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['state'],
      message: stateResult.message,
    });
  }

  // ── Zip / Postal Code cross-field validation ──
  if (data.zipCode && !validatePostalCode(data.zipCode, data.country)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['zipCode'],
      message: `Invalid postal code format for ${data.country || 'the selected country'}.`,
    });
  }
});

export type PersonalDetailsFields = z.infer<typeof PersonalDetailsSchema>;

export type PersonalDetailsInput = z.input<typeof PersonalDetailsSchema>;

export const PatientProfileResponseSchema = z.object({
  profile: z.record(z.any()).nullish(),
  hasPaidBefore: z.boolean(),
  itemType: z
    .enum(['patient', 'doctor', 'admin', 'nurse'])
    .catch('patient'),
  pk: z.string().catch(''),
  paymentStatus: z.string().catch(''),
  // Absent on accounts created before the flag existed, so default to false
  // (i.e. show onboarding) rather than failing the whole profile parse.
  onboardingCompleted: z.boolean().catch(false),
});

export type PatientProfileResponse = z.infer<typeof PatientProfileResponseSchema>;
